if (!customElements.get('litter-attach')) {
  const ATTACH_SOURCE = 'cart_litter';
  const FALLBACK_ERROR = 'Sorry, that did not work. Please try again.';
  const SECTIONS_PER_REQUEST = 5;
  // The surfaces that show the offer. If either fails to render, the shopper would see a stale offer.
  const REQUIRED_SECTIONS = ['CartDrawer', 'main-cart-items'];
  const viewedContexts = new Set();
  let isPending = false;

  // Every cart surface on the current page, so the drawer and the /cart page never drift apart.
  const collectSections = () => {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const sections = [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'main-cart-items', section: items?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: footer?.dataset.id, selector: '.js-contents' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'cart-notification-button', section: 'cart-notification-button', selector: '.shopify-section' },
      { id: 'cart-litter-attach', section: 'cart-litter-attach', selector: '.shopify-section' },
    ];
    return sections.filter((entry) => entry.section && document.getElementById(entry.id));
  };

  const setCartLocked = (isLocked) => {
    document.querySelectorAll('[data-litter-add]').forEach((button) => {
      // A button whose litter is already in the cart stays off until the cart is re-rendered.
      if (button.dataset.litterCommitted) return;
      button.setAttribute('aria-disabled', String(isLocked));
    });
    document.querySelectorAll('[data-litter-plan]').forEach((select) => {
      select.disabled = isLocked;
    });
    ['CartDrawer-CartItems', 'main-cart-items'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('cart__items--disabled', isLocked);
    });
  };

  customElements.define(
    'litter-attach',
    class LitterAttach extends HTMLElement {
      connectedCallback() {
        this.status = this.querySelector('.litter-attach__status');
        this.querySelectorAll('[data-litter-add]').forEach((button) =>
          button.addEventListener('click', this.onAdd.bind(this))
        );
        this.querySelectorAll('[data-litter-plan]').forEach((select) => {
          // Dawn's cart items listen for any change event as a quantity edit, so keep this one local.
          select.addEventListener('change', (event) => {
            event.stopPropagation();
            this.showPlanPrice(select);
          });
          // Browsers can restore a previous choice on back/forward, so sync the price on load.
          if (select.value) this.showPlanPrice(select);
        });
        this.trackViewWhenSeen();
      }

      showPlanPrice(select) {
        const item = select.closest('.litter-attach__item');
        const option = select.selectedOptions[0];
        if (!item || !option) return;
        const now = item.querySelector('[data-litter-price-now]');
        const was = item.querySelector('[data-litter-price-was]');
        if (now) now.textContent = option.dataset.price;
        if (was) {
          was.textContent = option.dataset.compare || '';
          was.hidden = !option.dataset.compare;
        }
      }

      // The delivery choice for the row the button sits in. Null means a one-off purchase.
      selectedPlan(button) {
        const select = button.closest('.litter-attach__item')?.querySelector('[data-litter-plan]');
        const option = select?.selectedOptions[0];
        if (!option?.value) return null;
        return { id: Number(option.value), price: Number(option.dataset.priceValue) };
      }

      disconnectedCallback() {
        this.drawerObserver?.disconnect();
      }

      get context() {
        return this.dataset.context || 'page';
      }

      // The drawer is in the page on every load, so only count a view once it is opened.
      trackViewWhenSeen() {
        if (viewedContexts.has(this.context)) return;
        const drawer = this.context === 'drawer' ? this.closest('cart-drawer') : null;
        if (!drawer || drawer.classList.contains('active')) {
          this.recordView();
          return;
        }
        this.drawerObserver = new MutationObserver(() => {
          if (this.isConnected && drawer.classList.contains('active')) this.recordView();
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      recordView() {
        this.drawerObserver?.disconnect();
        if (viewedContexts.has(this.context)) return;
        viewedContexts.add(this.context);
        this.track('litter_attach_view', {});
      }

      async onAdd(event) {
        const button = event.currentTarget;
        if (isPending || button.dataset.litterCommitted) return;
        isPending = true;
        this.setBusy(button, true);
        this.setStatus('');

        const plan = this.selectedPlan(button);
        let added;
        try {
          added = await this.addToCart(button.dataset.variantId, plan?.id);
        } catch (error) {
          console.error('[litter-attach] Add failed', error);
          if (error.isRejected) {
            this.setBusy(button, false);
            this.setStatus(error.userMessage || window.cartStrings?.error || FALLBACK_ERROR);
          } else {
            await this.showCartAfterUnclearAdd(button);
          }
          isPending = false;
          return;
        }

        // The litter is in the cart from here on, so nothing below may offer a retry.
        try {
          const discountApplied = await this.applyDiscountSafely();
          this.track('litter_attach_add', {
            variant_id: Number(button.dataset.variantId),
            product_title: button.dataset.productTitle,
            price: plan ? plan.price : Number(button.dataset.price),
            purchase_type: plan ? 'subscription' : 'one_off',
            selling_plan_id: plan ? plan.id : null,
            discount_code: discountApplied ? this.dataset.discountCode : null,
          });
          await this.refreshCart(button, added);
          if (discountApplied === false) this.announce('The discount code could not be applied to this order.');
        } catch (error) {
          console.error('[litter-attach] Cart refresh failed', error);
          this.markAddedWithoutRefresh(button);
        } finally {
          isPending = false;
        }
      }

      async addToCart(variantId, sellingPlanId) {
        const item = { id: Number(variantId), quantity: 1, properties: { _attach_source: ATTACH_SOURCE } };
        if (sellingPlanId) item.selling_plan = sellingPlanId;
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [item] }),
        });
        const data = await response.json().catch(() => null);
        if (response.ok && data && !data.status) return data;

        const error = new Error(data?.description || data?.message || `Add to cart failed: ${response.status}`);
        // Only a 4xx carrying Shopify's error body is a clear "not added". Anything else may have gone through.
        error.isRejected = response.status >= 400 && response.status < 500 && Boolean(data?.status);
        error.userMessage = error.isRejected ? data.description || data.message : null;
        throw error;
      }

      // No clear answer from Shopify, so the litter may already be in the cart. Show the real cart rather
      // than a retry: the offer is gone if the litter went in, and still there if it did not.
      async showCartAfterUnclearAdd(button) {
        try {
          await this.refreshCart(button, null);
          this.announce('Please check your cart before adding again.');
        } catch (error) {
          console.error('[litter-attach] Cart refresh failed', error);
          this.commitButton(button, null);
          this.setStatus('Something went wrong. Refresh the page to check your cart.');
        }
      }

      // Returns null when no code is set, otherwise whether the code applies to the cart.
      async applyDiscountSafely() {
        const code = this.dataset.discountCode;
        if (!code) return null;
        try {
          return await this.applyDiscount(code);
        } catch (error) {
          console.warn('[litter-attach] Discount step failed', error);
          return false;
        }
      }

      // Adds the code to any codes already on the cart, rather than replacing them.
      // A code Shopify reports as not applicable is taken off again, so checkout is not left with a dead code.
      async applyDiscount(code) {
        const isCode = (candidate) => candidate.toLowerCase() === code.toLowerCase();
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        if (!cartResponse.ok) throw new Error(`Cart read failed: ${cartResponse.status}`);
        const existing = ((await cartResponse.json()).discount_codes || []).map((discount) => discount.code);
        if (existing.some(isCode)) return true;

        const cart = await this.updateDiscounts([...existing, code]);
        const applied = (cart.discount_codes || []).some((discount) => isCode(discount.code) && discount.applicable);
        if (!applied) await this.updateDiscounts(existing);
        return applied;
      }

      async updateDiscounts(codes) {
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        if (!response.ok) throw new Error(`Discount update failed: ${response.status}`);
        return response.json();
      }

      // The Section Rendering API returns at most five sections per request.
      async fetchSections(sections) {
        const ids = sections.map((entry) => entry.section);
        const batches = [];
        for (let index = 0; index < ids.length; index += SECTIONS_PER_REQUEST) {
          batches.push(ids.slice(index, index + SECTIONS_PER_REQUEST));
        }
        const results = await Promise.all(
          batches.map(async (batch) => {
            // From the cart URL, so a page that cannot render (a 404, for one) does not break the refresh.
            const response = await fetch(`${routes.cart_url}?sections=${batch.join(',')}`);
            if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
            return response.json();
          })
        );
        return Object.assign({}, ...results);
      }

      replaceSections(sections, html) {
        sections.forEach((entry) => {
          const target = document.getElementById(entry.id);
          if (!target || !html[entry.section]) return;

          const source = new DOMParser()
            .parseFromString(html[entry.section], 'text/html')
            .querySelector(entry.selector);
          const destination = target.querySelector(entry.selector) || target;
          if (source) destination.innerHTML = source.innerHTML;
        });
      }

      // Throws if the cart could not be re-rendered; the caller decides what the shopper is told.
      // `added` is null when it is not known whether the add went through.
      async refreshCart(button, added) {
        const sections = collectSections();
        const html = await this.fetchSections(sections);
        const missing = sections.find((entry) => REQUIRED_SECTIONS.includes(entry.id) && !html[entry.section]);
        if (missing) throw new Error(`Section did not render: ${missing.section}`);
        this.replaceSections(sections, html);
        setCartLocked(false);

        // Source 'cart-items' tells Dawn's cart surfaces not to refetch what was just rendered,
        // while other listeners (quantity labels, apps) still hear about the change.
        if (added && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: added });
        }
        if (!added) return;
        if (this.context === 'notification') this.confirmInNotification(button.dataset.productTitle);
        this.restoreFocus(button.dataset.productUrl);
      }

      markAddedWithoutRefresh(button) {
        this.commitButton(button, 'Added');
        this.setStatus('Added to your cart. Refresh the page to see it.');
      }

      // Switches the button off for good, so a litter that may be in the cart cannot be added twice.
      commitButton(button, labelText) {
        button.dataset.litterCommitted = 'true';
        setCartLocked(false);
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'false');
        button.querySelector('.litter-attach__add-busy')?.setAttribute('hidden', '');
        const label = button.querySelector('.litter-attach__add-label');
        if (label) {
          if (labelText) label.textContent = labelText;
          label.removeAttribute('hidden');
        }
      }

      confirmInNotification(productTitle) {
        const confirmation = document.createElement('p');
        confirmation.className = 'litter-attach__added';
        confirmation.setAttribute('role', 'status');
        confirmation.textContent = `Added: ${productTitle}`;
        document.getElementById('cart-litter-attach')?.prepend(confirmation);
      }

      // Moves focus only while the surface the shopper used is still open.
      restoreFocus(productUrl) {
        if (typeof trapFocus !== 'function') return;

        if (this.context === 'drawer') {
          const drawer = document.getElementById('CartDrawer');
          if (!drawer || !document.querySelector('cart-drawer')?.classList.contains('active')) return;
          trapFocus(drawer, this.findLine(drawer, productUrl) || drawer.querySelector('.drawer__inner'));
        } else if (this.context === 'notification') {
          const notification = document.getElementById('cart-notification');
          const viewCart = document.getElementById('cart-notification-button');
          if (notification?.classList.contains('active') && viewCart) trapFocus(notification, viewCart);
        } else if (document.activeElement === document.body) {
          this.findLine(document.getElementById('main-cart-items'), productUrl)?.focus();
        }
      }

      findLine(container, productUrl) {
        if (!container || !productUrl) return null;
        const path = CSS.escape(productUrl.split('?')[0]);
        return container.querySelector(`.cart-item__name[href="${path}"], .cart-item__name[href^="${path}?"]`);
      }

      announce(message) {
        const target =
          document.querySelector(`litter-attach[data-context="${this.context}"] .litter-attach__status`) ||
          document.getElementById(this.context === 'drawer' ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
        if (target) target.textContent = message;
      }

      setBusy(button, isBusy) {
        setCartLocked(isBusy);
        button.setAttribute('aria-busy', String(isBusy));
        button.querySelector('.litter-attach__add-label')?.toggleAttribute('hidden', isBusy);
        button.querySelector('.litter-attach__add-busy')?.toggleAttribute('hidden', !isBusy);
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      track(eventName, detail) {
        const payload = {
          context: this.context,
          robot_in_cart: this.dataset.robot === 'true',
          currency: window.Shopify?.currency?.active,
          ...detail,
        };
        // Tracking must never block the cart, so both destinations are guarded.
        try {
          window.Shopify?.analytics?.publish?.(eventName, payload);
          if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...payload });
        } catch (error) {
          console.warn('[litter-attach] Analytics publish failed', error);
        }
      }
    }
  );
}
